<template>
  <div v-if="show" class="modal">
    <div class="popup-content">
      <!-- Fixed Header -->
      <div class="popup-header">
        <div class="header-left">
          <h3 v-if="loading">Dealer Details</h3>
          <div v-if="!loading">
            <div class="header-main">
              <h3>{{ dealerDetails.businessName }}</h3>
              <div class="header-status">
                <span class="status-dot" :class="statusDotClass"></span>
                <span class="status-text">{{ statusText }}</span>
                <span class="verified-badge ms-2">
                  <i :class="verifiedIcon"></i>
                  {{ verifiedText }}
                </span>
              </div>
            </div>

            <!-- Created By and Created At Info Boxes - Single Line -->
            <div class="creation-info-container">
              <div class="info-box">
                <span class="info-label">Created By:</span>
                <span class="info-value">{{
                  dealerDetails.createdBy || "N/A"
                }}</span>
              </div>
              <div class="info-box">
                <span class="info-label">Created At:</span>
                <span class="info-value">{{
                  formatDateTime(dealerDetails.createdAt)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="header-right">
          <div v-if="!loading" class="tooltip-container">
            <button
              @click="fetchDealerDetails(dealerId)"
              class="btn btn-light refresh-btn"
            >
              <i class="fa-solid fa-arrows-rotate"></i>
            </button>
            <span class="tooltip-text">Refresh</span>
          </div>
          &nbsp;
          <div class="tooltip-container">
            <button @click="close" class="btn btn-light close-btn">
              <i class="fa-solid fa-xmark"></i>
            </button>
            <span class="tooltip-text">Close</span>
          </div>
        </div>
      </div>

      <!-- Show Loading Spinner -->
      <div v-if="loading" class="loading-container">
        <i class="fa fa-spinner fa-spin"></i>
        <span>Loading...</span>
      </div>

      <!-- Tab Navigation -->
      <div v-if="!loading" class="section-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="['tab-btn tab-btn-tabs', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          <i :class="tab.icon"></i>
          {{ tab.name }}
        </button>
      </div>

      <!-- Body (Scrollable) -->
      <div v-if="!loading" class="popup-body">
        <div class="details-container">
          <!-- Owner Information Tab -->
          <div v-if="activeTab === 'owner'" class="tab-content">
            <div class="popup-columns">
              <!-- Owner Details Column -->
              <div class="popup-column details-column">
                <div class="info-section">
                  <h5 class="section-title">Owner Details</h5>
                  <div class="info-list">
                    <div class="detail-item">
                      <strong>Title:</strong> {{ dealerDetails.title }}
                    </div>
                    <div class="detail-item">
                      <strong>First Name:</strong> {{ dealerDetails.firstName }}
                    </div>
                    <div class="detail-item">
                      <strong>Last Name:</strong> {{ dealerDetails.lastName }}
                    </div>
                    <div class="detail-item">
                      <strong>Email Address:</strong> {{ dealerDetails.email }}
                    </div>
                    <div class="detail-item">
                      <strong>Mobile Number:</strong> {{ dealerDetails.mobile }}
                    </div>
                    <div class="detail-item">
                      <strong>Date of Birth:</strong>
                      {{ formatDate(dealerDetails.dob) }}
                    </div>
                    <div class="detail-item">
                      <strong>Identity Type:</strong>
                      {{ dealerDetails.identityType }}
                    </div>
                    <div class="detail-item">
                      <strong>Identity Number:</strong>
                      {{ dealerDetails.identityNumber }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- ID Documents Column -->
              <div class="popup-column documents-column">
                <div class="info-section">
                  <h5 class="section-title">ID Documents</h5>
                  <div class="documents-grid">
                    <!-- ID Front Image -->
                    <div class="document-item">
                      <div
                        class="document-image-container"
                        @click="openDocument(dealerDetails.idFrontImage)"
                      >
                        <!-- Show image if valid image type -->
                        <img
                          v-if="
                            isValidImage(dealerDetails.idFrontImage) &&
                            isImageType(dealerDetails.idFrontImageType)
                          "
                          :src="dealerDetails.idFrontImage"
                          alt="ID Front"
                          class="document-image"
                          @error="handleImageError('idFrontImage')"
                        />
                        <!-- Show PDF thumbnail if PDF type -->
                        <div
                          v-else-if="
                            isValidImage(dealerDetails.idFrontImage) &&
                            isPdfType(dealerDetails.idFrontImageType)
                          "
                          class="pdf-thumbnail"
                        >
                          <i class="fas fa-file-pdf pdf-icon"></i>
                          <span class="pdf-text">PDF Document</span>
                          <span class="pdf-subtext">Click to view</span>
                        </div>
                        <!-- Show invalid thumbnail if document is invalid -->
                        <div v-else class="invalid-thumbnail">
                          <i class="fas fa-file-excel invalid-icon"></i>
                          <span class="invalid-text">Invalid Document</span>
                        </div>
                        <div
                          class="image-overlay"
                          v-if="isValidImage(dealerDetails.idFrontImage)"
                        >
                          <i class="fas fa-search-plus"></i>
                        </div>
                      </div>
                      <div class="document-label">ID Front</div>
                    </div>

                    <!-- ID Back Image -->
                    <div
                      class="document-item"
                      v-if="
                        dealerDetails.identityType !== 'Passport' &&
                        dealerDetails.identityType !== 'Driving License'
                      "
                    >
                      <div
                        class="document-image-container"
                        @click="openDocument(dealerDetails.idBackImage)"
                      >
                        <!-- Show image if valid image type -->
                        <img
                          v-if="
                            isValidImage(dealerDetails.idBackImage) &&
                            isImageType(dealerDetails.idBackImageType)
                          "
                          :src="dealerDetails.idBackImage"
                          alt="ID Back"
                          class="document-image"
                          @error="handleImageError('idBackImage')"
                        />
                        <!-- Show PDF thumbnail if PDF type -->
                        <div
                          v-else-if="
                            isValidImage(dealerDetails.idBackImage) &&
                            isPdfType(dealerDetails.idBackImageType)
                          "
                          class="pdf-thumbnail"
                        >
                          <i class="fas fa-file-pdf pdf-icon"></i>
                          <span class="pdf-text">PDF Document</span>
                          <span class="pdf-subtext">Click to view</span>
                        </div>
                        <!-- Show invalid thumbnail if document is invalid -->
                        <div v-else class="invalid-thumbnail">
                          <i class="fas fa-file-excel invalid-icon"></i>
                          <span class="invalid-text">Invalid Document</span>
                        </div>
                        <div
                          class="image-overlay"
                          v-if="isValidImage(dealerDetails.idBackImage)"
                        >
                          <i class="fas fa-search-plus"></i>
                        </div>
                      </div>
                      <div class="document-label">ID Back</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Business Information Tab -->
          <div v-if="activeTab === 'business'" class="tab-content">
            <div class="popup-columns">
              <!-- Business Details Column -->
              <div class="popup-column details-column">
                <div class="info-section">
                  <h5 class="section-title">Business Details</h5>
                  <div class="info-list">
                    <div class="detail-item">
                      <strong>Business Name:</strong>
                      {{ dealerDetails.businessName }}
                    </div>
                    <div class="detail-item">
                      <strong>Company Name:</strong>
                      {{ dealerDetails.companyName }}
                    </div>
                    <div class="detail-item">
                      <strong>Pricing Category:</strong>
                      {{ dealerDetails.pricingCatName }}
                    </div>
                    <div class="detail-item">
                      <strong>Dealer Type:</strong>
                      {{ dealerDetails.dealerType }}
                    </div>
                    <div class="detail-item">
                      <strong>Nature of Business:</strong>
                      {{ dealerDetails.natureOfBusiness }}
                    </div>
                    <div class="detail-item">
                      <strong>Route:</strong> {{ dealerDetails.routeName }}
                    </div>
                    <div class="detail-item">
                      <strong>Business Registration Number:</strong>
                      {{ dealerDetails.businessRegNumber }}
                    </div>
                    <div class="detail-item">
                      <strong>Address:</strong> {{ dealerDetails.address }}
                    </div>
                    <div class="detail-item">
                      <strong>City:</strong> {{ dealerDetails.city }}
                    </div>
                    <div class="detail-item">
                      <strong>District:</strong>
                      {{ dealerDetails.districtName }}
                    </div>
                    <div class="detail-item">
                      <strong>Postal Code:</strong> {{ dealerDetails.postCode }}
                    </div>
                    <div class="detail-item">
                      <strong>Distributor:</strong>
                      {{ dealerDetails.distributorName }}
                    </div>
                    <div class="detail-item" v-if="dealerDetails.dealerGroupName && dealerDetails.dealerGroupName !== 'N/A'">
                      <strong>Dealer Group:</strong>
                      {{ dealerDetails.dealerGroupName }}
                    </div>
                    <div class="detail-item">
                      <strong>Sales Executive:</strong>
                      {{ dealerDetails.salesExecutiveName }}
                    </div>
                    <div class="detail-item">
                      <strong>SAP Code:</strong> {{ dealerDetails.sapCode }}
                    </div>
                    <div class="detail-item">
                      <strong>Dealer App Registered:</strong>
                      {{ appRegisteredStatus }}
                    </div>
                    <div
                      class="detail-item"
                      v-if="dealerDetails.appRegistered === '1'"
                    >
                      <strong>Device Id:</strong> {{ dealerDetails.deviceId }}
                    </div>
                    <div class="detail-item geo-field">
                      <div class="geo-info">
                        <strong>Geo-location:</strong>
                        ({{ dealerDetails.latitude }},
                        {{ dealerDetails.longitude }})
                      </div>
                      <button
                        v-if="dealerDetails.latitude && dealerDetails.longitude"
                        class="btn btn-light map-btn me-2"
                        @click="
                          openMap(
                            dealerDetails.latitude,
                            dealerDetails.longitude,
                          )
                        "
                        title="View on Map"
                      >
                        <i class="fas fa-map-marker-alt"></i>
                      </button>
                    </div>
                    <div class="detail-item">
                      <strong>Is home delivery available:</strong>
                      {{ getHomeDeliveryLabel(dealerDetails.is_home_delivery) }}
                    </div>
                    <div class="detail-item">
                      <strong>Eligible for Trust:</strong>
                      {{ getEligibleTrustLabel(dealerDetails.is_eligible_trust) }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Business Documents Column -->
              <div class="popup-column documents-column">
                <div class="info-section">
                  <h5 class="section-title">Business Documents</h5>
                  <div class="documents-grid">
                    <!-- Business Registration Document -->
                    <div class="document-item">
                      <div
                        class="document-image-container"
                        @click="openDocument(dealerDetails.brDocument)"
                      >
                        <!-- Show image if valid image type -->
                        <img
                          v-if="
                            isValidImage(dealerDetails.brDocument) &&
                            isImageType(dealerDetails.brDocumentType)
                          "
                          :src="dealerDetails.brDocument"
                          alt="Business Registration"
                          class="document-image"
                          @error="handleImageError('brDocument')"
                        />
                        <!-- Show PDF thumbnail if PDF type -->
                        <div
                          v-else-if="
                            isValidImage(dealerDetails.brDocument) &&
                            isPdfType(dealerDetails.brDocumentType)
                          "
                          class="pdf-thumbnail"
                        >
                          <i class="fas fa-file-pdf pdf-icon"></i>
                          <span class="pdf-text">PDF Document</span>
                          <span class="pdf-subtext">Click to view</span>
                        </div>
                        <!-- Show invalid thumbnail if document is invalid -->
                        <div v-else class="invalid-thumbnail">
                          <i class="fas fa-file-excel invalid-icon"></i>
                          <span class="invalid-text">Invalid Document</span>
                        </div>
                        <div
                          class="image-overlay"
                          v-if="isValidImage(dealerDetails.brDocument)"
                        >
                          <i class="fas fa-search-plus"></i>
                        </div>
                      </div>
                      <div class="document-label">Business Registration</div>
                    </div>

                    <!-- Agreement Document -->
                    <div class="document-item">
                      <div
                        class="document-image-container"
                        @click="openDocument(dealerDetails.agreementDocument)"
                      >
                        <!-- Show image if valid image type -->
                        <img
                          v-if="
                            isValidImage(dealerDetails.agreementDocument) &&
                            isImageType(dealerDetails.agreementDocumentType)
                          "
                          :src="dealerDetails.agreementDocument"
                          alt="Agreement"
                          class="document-image"
                          @error="handleImageError('agreementDocument')"
                        />
                        <!-- Show PDF thumbnail if PDF type -->
                        <div
                          v-else-if="
                            isValidImage(dealerDetails.agreementDocument) &&
                            isPdfType(dealerDetails.agreementDocumentType)
                          "
                          class="pdf-thumbnail"
                        >
                          <i class="fas fa-file-pdf pdf-icon"></i>
                          <span class="pdf-text">PDF Document</span>
                          <span class="pdf-subtext">Click to view</span>
                        </div>
                        <!-- Show invalid thumbnail if document is invalid -->
                        <div v-else class="invalid-thumbnail">
                          <i class="fas fa-file-excel invalid-icon"></i>
                          <span class="invalid-text">Invalid Document</span>
                        </div>
                        <div
                          class="image-overlay"
                          v-if="isValidImage(dealerDetails.agreementDocument)"
                        >
                          <i class="fas fa-search-plus"></i>
                        </div>
                      </div>
                      <div class="document-label">Agreement</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Financial Information Tab -->
          <div v-if="activeTab === 'finance'" class="tab-content">
            <div class="info-section finance-grid">
              <h5 class="section-title">Financial Information</h5>

              <div class="finance-row">
                <div class="finance-item">
                  <strong>Is VAT Liable:</strong>
                  {{
                    dealerDetails.vatLiable === "1" ||
                    dealerDetails.vatLiable === 1
                      ? "Yes"
                      : "No"
                  }}
                </div>

                <div class="finance-item">
                  <strong>VAT Number:</strong>
                  {{ dealerDetails.vatNo || "N/A" }}
                </div>
              </div>

              <div class="finance-row">
                <div class="finance-item">
                  <strong>VAT Name:</strong>
                  {{ dealerDetails.vatName || "N/A" }}
                </div>

                <div class="finance-item">
                  <strong>VAT Contact No:</strong>
                  {{ dealerDetails.vatContactNo || "N/A" }}
                </div>
              </div>

              <div class="finance-row">
                <div class="finance-item full-row-item">
                  <strong>VAT Address:</strong>
                  {{ dealerDetails.vatAddress || "N/A" }}
                </div>
              </div>

              <div class="finance-row">
                <div class="finance-item">
                  <strong>Is SVAT Liable:</strong>
                  {{
                    dealerDetails.svatLiable === "1" ||
                    dealerDetails.svatLiable === 1
                      ? "Yes"
                      : "No"
                  }}
                </div>

                <div class="finance-item">
                  <strong>SVAT Number:</strong>
                  {{ dealerDetails.svatNo || "N/A" }}
                </div>
              </div>

              <div class="finance-row">
                <div class="finance-item">
                  <strong>Credit Limit Amount:</strong>
                  {{
                    dealerDetails.creditLimit
                      ? formatCurrency(dealerDetails.creditLimit)
                      : "N/A"
                  }}
                </div>

                <div class="finance-item">
                  <strong>Credit Limit Days:</strong>
                  {{ dealerDetails.creditLimitDays || "N/A" }}
                </div>
              </div>

              <div class="finance-row">
                <div class="finance-item">
                  <strong>Is eligible for printed invoice:</strong>
                  {{
                    getEligiblePrintedInvoiceLabel(
                      dealerDetails.eligiblePrintedInvoice,
                    )
                  }}
                </div>
              </div>
            </div>
          </div>

          <!-- Stock Management Tab -->
          <div v-if="activeTab === 'stock'" class="tab-content">
            <div class="info-section">
              <h5 class="section-title">Stock Management</h5>
              <div class="stock-table-container">
                <table class="table-container">
                  <thead class="stock-table">
                    <tr>
                      <th class="item-image-col">Item Image</th>
                      <th class="item-name-col">Item Name</th>
                      <th class="reorder-col">
                        <div class="reorder-header">
                          <span>Re-Order Levels</span>
                          <div class="min-max-labels">
                            <span>Min</span>
                            <span>Max</span>
                          </div>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in stockItems" :key="index">
                      <td class="item-image-cell">
                        <div class="item-image-container">
                          <img
                            v-if="
                              item.itemImage &&
                              isValidBase64Image(item.itemImage)
                            "
                            :src="getBase64ImageSrc(item.itemImage)"
                            :alt="item.itemName"
                            class="item-image"
                            @error="handleStockImageError(index)"
                          />
                          <div v-else class="item-placeholder">
                            <i class="fas fa-box"></i>
                          </div>
                          <div
                            class="image-overlay"
                            v-if="
                              item.itemImage &&
                              isValidBase64Image(item.itemImage)
                            "
                          >
                            <i class="fas fa-search-plus"></i>
                          </div>
                        </div>
                      </td>
                      <td class="item-name-cell">
                        {{ item.itemName || "N/A" }}
                      </td>
                      <td class="reorder-cell">
                        <div class="reorder-levels">
                          <div class="min-level">
                            {{ item.minQty || "N/A" }}
                          </div>
                          <div class="max-level">
                            {{ item.maxQty || "N/A" }}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Empty state -->
                <div v-if="stockItems.length === 0" class="empty-state">
                  <i class="fas fa-box-open"></i>
                  <p>No stock items found</p>
                </div>
              </div>
            </div>
          </div>

         
        </div>
      </div>
    </div>

    <!-- Reusable Map Modal -->
    <ViewLocationModal
      :show="showMapModal"
      :latitude="currentLat"
      :longitude="currentLng"
      :title="mapModalTitle"
      :marker-title="dealerDetails.business_name || 'Dealer Location'"
      @close="closeMapModal"
    />
  </div>
</template>

<script>
import { viewDealerDetails } from "@/services/dataService";
import ViewLocationModal from "@/pages/Modal/maps/viewLocation.vue";

export default {
  name: "ViewDealerInfoModal",
  components: {
    ViewLocationModal,
  },
  props: {
    show: { type: Boolean, default: false },
    dealerId: { type: [String, Number], default: null },
  },
  emits: ["close"],
  data() {
    return {
      loading: false,
      dealerDetails: {},
      showMapModal: false,
      currentLat: null,
      currentLng: null,
      activeTab: "owner",
      tabs: [
        { id: "owner", name: "Owner Information", icon: "fas fa-user" },
        {
          id: "business",
          name: "Business Information",
          icon: "fas fa-building",
        },
        {
          id: "finance",
          name: "Financial Information",
          icon: "fa-solid fa-coins",
        },
        { id: "stock", name: "Stock Management", icon: "fas fa-boxes" },
      ],
      stockItems: [],
      stockImageErrors: {}, // Track image loading errors for stock items

      imageErrors: {
        idFrontImage: false,
        idBackImage: false,
        brDocument: false,
        agreementDocument: false,
      },
    };
  },
  computed: {
    statusDotClass() {
      return {
        "status-active": this.dealerDetails.isActive === "1",
        "status-inactive": this.dealerDetails.isActive === "0",
      };
    },
    statusText() {
      return this.dealerDetails.isActive === "1" ? "Active" : "Inactive";
    },
    mapModalTitle() {
      return `${this.dealerDetails.businessName || "Dealer"} Location`;
    },
    appRegisteredStatus() {
      return this.dealerDetails.appRegistered === "1" ? "Yes" : "No";
    },
    verifiedText() {
      return this.dealerDetails.is_verified === "1"
        ? "Verified"
        : "Not Verified";
    },
    verifiedIcon() {
      return this.dealerDetails.is_verified === "1"
        ? "fas fa-check-circle text-success"
        : "fas fa-times-circle text-danger";
    },
  },
  watch: {
    show(newVal) {
      if (newVal && this.dealerId) {
        this.fetchDealerDetails(this.dealerId);
      } else {
        this.dealerDetails = {};
        this.stockItems = [];
        this.stockImageErrors = {};
        this.resetImageErrors();
      }
    },
  },
  methods: {
    // Reset image errors
    resetImageErrors() {
      this.imageErrors = {
        idFrontImage: false,
        idBackImage: false,
        brDocument: false,
        agreementDocument: false,
      };
    },

    // Check if document type is image
    isImageType(docType) {
      if (!docType) return false;
      return docType.startsWith("image/");
    },

    // Check if document type is PDF
    isPdfType(docType) {
      if (!docType) return false;
      return docType === "application/pdf";
    },

    // Get dealer details from API
    async fetchDealerDetails(id) {
      try {
        this.loading = true;
        this.resetImageErrors();
        const token = localStorage.getItem("token_set") || "";
        const response = await viewDealerDetails(id, token);

        if (response.data && response.data.data) {
          const data = response.data.data;
          this.dealerDetails = {
            title: data.title ?? "N/A",
            firstName: data.first_name ?? "N/A",
            lastName: data.last_name ?? "N/A",
            email: data.email ?? "N/A",
            mobile: data.mobile ?? "N/A",
            dob: data.dob ?? "N/A",
            identityType: data.identity_type ?? "N/A",
            identityNumber: data.identity_number ?? "N/A",
            idFrontImage: data.id_front_image ?? "N/A",
            idFrontImageType: data.id_front_mime ?? "N/A",
            idBackImage: data.id_back_image ?? "N/A",
            idBackImageType: data.id_back_mime ?? "N/A",
            isActive: data.is_active ?? "N/A",
            businessName: data.business_name ?? "N/A",
            companyName: data.company_name ?? "N/A",
            distributorName: data.distributor_name ?? "N/A",
            dealerGroupName: data.dealer_group_name ?? null,
            salesExecutiveName: data.sales_executive_name ?? "N/A",
            pricingCatName: data.dealer_cat_name ?? "N/A",
            dealerType: data.dealer_type ?? "N/A",
            natureOfBusiness: data.nature_of_business ?? "N/A",
            routeName: data.route_name ?? "N/A",
            address: data.address1 ?? "N/A",
            city: data.city ?? "N/A",
            districtName: data.district_name ?? "N/A",
            postCode: data.post_code ?? "N/A",
            latitude: data.latitude ?? "N/A",
            longitude: data.longitude ?? "N/A",
            businessRegNumber: data.business_reg_number ?? "N/A",
            brDocument: data.br_document ?? "N/A",
            brDocumentType: data.br_mime ?? "N/A",
            agreementDocument: data.agreement_document ?? "N/A",
            agreementDocumentType: data.agreement_mime ?? "N/A",
            sapCode: data.sap_code ?? "N/A",
            vatLiable: data.vat_liable ?? "N/A",
            vatNo: data.vat_no ?? "N/A",
            vatAddress: data.parent_address ?? "N/A",
            vatName: data.parent_name ?? "N/A",
            vatContactNo: data.parent_contact_no ?? "N/A",
            svatLiable: data.svat_liable ?? "N/A",
            svatNo: data.svat_no ?? "N/A",
            creditLimitDays: data.credit_limit_days ?? "N/A",
            creditLimit: data.credit_limit ?? "N/A",
            appRegistered: data.app_registered ?? "N/A",
            deviceId: data.device_id ?? "N/A",
            eligiblePrintedInvoice: data.is_print ?? "N/A",
            is_home_delivery: data.is_home_delivery ?? "N/A",
            is_eligible_trust: data.is_eligible_trust ?? "N/A",
            is_verified: data.is_verified ?? "N/A",
            createdBy: data.created_by ?? "N/A",
            createdAt: data.created_at ?? "N/A",
          };

          // Process dealer stock from API response
          this.processDealerStock(data.dealer_stock);
        }
      } catch (error) {
        console.error("Error fetching dealer details:", error);
      } finally {
        this.loading = false;
      }
    },

    // Process dealer stock data
    processDealerStock(dealerStock) {
      if (dealerStock && Array.isArray(dealerStock)) {
        this.stockItems = dealerStock.map((stock) => ({
          id: stock.itemId,
          itemName: stock.itemName || "Unknown Item",
          itemImage: stock.itemImage || null,
          minQty: stock.minQty || "0",
          maxQty: stock.maxQty || "0",
        }));
      } else {
        this.stockItems = [];
      }
    },

    // Get label for eligible printed invoice
    getEligiblePrintedInvoiceLabel(eligiblePrintedInvoice) {
      if (eligiblePrintedInvoice === "1") {
        return "Yes";
      } else {
        return "No";
      }
    },

    // Get label for home delivery
    getHomeDeliveryLabel(is_home_delivery) {
      if (is_home_delivery === "1") {
        return "Yes";
      } else {
        return "No";
      }
    },

    // Get label for eligible trust
    getEligibleTrustLabel(is_eligible_trust) {
      if (is_eligible_trust === "1") {
        return "Yes";
      } else {
        return "No";
      }
    },

    // Handle image loading errors
    handleImageError(field) {
      this.imageErrors[field] = true;
    },

    // Format date and time
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return "N/A";
      const date = new Date(dateTimeString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    // Check if base64 image is valid
    isValidBase64Image(base64String) {
      if (!base64String) return false;

      // Basic base64 validation
      const base64Regex =
        /^data:image\/(png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

      if (base64Regex.test(base64String)) {
        return true;
      }

      // Also check if it's just base64 without data URL prefix
      const pureBase64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
      if (pureBase64Regex.test(base64String)) {
        return base64String.length > 0;
      }

      return false;
    },

    // Convert base64 string to proper image src
    getBase64ImageSrc(base64String) {
      if (!base64String) return "";

      // If it already has data URL prefix, return as is
      if (base64String.startsWith("data:image/")) {
        return base64String;
      }

      // Otherwise, assume it's JPEG and add the prefix
      return `data:image/jpeg;base64,${base64String}`;
    },

    // Handle stock image loading errors
    handleStockImageError(index) {
      this.$set(this.stockImageErrors, index, true);
    },

    // Open document in new tab
    openDocument(url) {
      if (!url || url === "N/A" || url.trim() === "") return;
      window.open(url, "_blank");
    },

    // Close the modal
    close() {
      this.$emit("close");
    },

    // Format date
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    // Format currency
    formatCurrency(amount) {
      if (!amount) return "N/A";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "LKR",
      }).format(amount);
    },

    // Check if image URL is valid
    isValidImage(url) {
      if (!url) return false;
      // Check if URL is valid and not empty/null
      return (
        url.trim() !== "" && url !== null && url !== undefined && url !== "N/A"
      );
    },

    // Open map modal
    openMap(lat, lng) {
      this.currentLat = parseFloat(lat);
      this.currentLng = parseFloat(lng);
      this.showMapModal = true;
    },

    // Close map modal
    closeMapModal() {
      this.showMapModal = false;
      this.currentLat = null;
      this.currentLng = null;
    },
  },
};
</script>

<style scoped lang="scss">
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(15, 23, 42, 0.7);
  z-index: 2000;
}

.popup-content {
  width: 95vw;
  height: 95vh;
  background-color: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
}

/* Fixed Header */
.popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: white;
  position: sticky;
  top: 0;
  z-index: 10;
  font-weight: 600;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-left h3 {
  margin: 0;
  font-size: 1.25rem;
}

.header-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-active {
  background-color: #16a34a;
}

.status-inactive {
  background-color: #dc2626;
}

.status-text {
  font-size: 0.9rem;
  color: #374151;
}

/* Add these styles for the compact creation info boxes */
.header-main {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 8px;
}

.creation-info-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.info-box {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f8f9fa;
  border: 0.5px solid #e5e7eb;
  border-radius: 2px;
  padding: 2px 4px;
  line-height: 1;
}

.info-label {
  font-weight: bold;
  color: #6b7280;
  font-size: 10px;
}

.info-value {
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  font-size: 10px;
}

/* Header Right with Tooltip Containers */
.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

/* Refresh Button */
.refresh-btn {
  border-radius: 50%;
  width: 33px;
  height: 33px;
  padding: 0;
  cursor: pointer;
  font-size: 18px;
}

/* Close Button */
.close-btn {
  border-radius: 50%;
  width: 33px;
  height: 33px;
  padding: 0;
  cursor: pointer;
  font-size: 18px;
}

/* Tooltip Styling - Positioned below buttons */
.tooltip-text {
  visibility: hidden;
  background-color: #333;
  color: #fff;
  text-align: center;
  padding: 6px 10px;
  border-radius: 4px;
  position: absolute;
  bottom: -35px; /* Position below the button */
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 12px;
  z-index: 1001;
  opacity: 0;
  transition: opacity 0.3s ease;
}

/* Show tooltip on hover */
.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

/* Tab Navigation */
.section-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 1rem 1.5rem;
  background: white;
  border-bottom: 1px solid #e5e7eb;
}

.tab-btn-tabs {
  padding: 8px 16px;
  cursor: pointer;
  background-color: #f4f4f4;
  border: 1px solid #ddd;
  border-radius: 10px 5px;
  white-space: nowrap;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  font-weight: 500;
}

.tab-btn-tabs.active {
  background: linear-gradient(45deg, #253069, #1c66ad);
  color: white;
}

.tab-btn-tabs:hover {
  background: linear-gradient(45deg, #1c66ad, #253069);
  color: white;
}

/* Scrollable Body */
.popup-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  background-color: #f9fafb;
}

.details-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Two-column layout for Owner and Business tabs */
.popup-columns {
  display: flex;
  gap: 20px;
  height: 100%;
}

.popup-column {
  display: flex;
  flex-direction: column;
}

.details-column {
  width: 60%;
  padding-right: 20px;
  border-right: 1px solid #e5e7eb;
}

.documents-column {
  width: 40%;
  padding-left: 10px;
}

/* Info Styling */
.info-section {
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
}

.section-title {
  margin-top: 0;
  margin-bottom: 1.2rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  color: #374151;
  font-size: 1.1rem;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

/* Keeps all info boxes same height even with icon */
.geo-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f8f9fa;
  padding: 10px 12px;
  border-radius: 6px;
  height: 100%;
  min-height: 45px;
  box-sizing: border-box;
}

.geo-info {
  flex: 1;
  color: #111827;
  font-size: 0.95rem;
  overflow-wrap: break-word;
}

.map-btn {
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-left: 8px;
}

.map-btn:hover {
  background-color: #059669;
  transform: scale(1.05);
}

.map-btn i {
  pointer-events: none;
}

/* Documents Section */
.documents-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 1rem;
}

.document-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.document-image-container {
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.document-image-container:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.document-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

/* PDF Thumbnail Styles */
.pdf-thumbnail {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fef3f3, #fee2e2);
  border: 2px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  transition: all 0.3s ease;
}

.pdf-icon {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  color: #dc2626;
}

.pdf-text {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
}

.pdf-subtext {
  font-size: 0.8rem;
  color: #6b7280;
}

.document-image-container:hover .pdf-thumbnail {
  background: linear-gradient(135deg, #fecaca, #fca5a5);
  border-color: #f87171;
}

/* Invalid Thumbnail Styles */
.invalid-thumbnail {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  transition: all 0.3s ease;
}

.invalid-icon {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  color: #6b7280;
}

.invalid-text {
  font-weight: 600;
  color: #374151;
}

.document-image-container:hover .invalid-thumbnail {
  background: linear-gradient(135deg, #e5e7eb, #d1d5db);
  border-color: #9ca3af;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.document-image-container:hover .image-overlay {
  opacity: 1;
}

.image-overlay i {
  color: white;
  font-size: 1.5rem;
}

.document-label {
  font-weight: 500;
  color: #374151;
  text-align: center;
}

/* Stock Management Styles */
.stock-table-container {
  overflow-x: auto;
}

.table-container {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.table-container th,
.table-container td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.table-container th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
}

/* Keep table headers on a single line */
.stock-table th {
  white-space: nowrap;
}

.reorder-header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.min-max-labels {
  display: flex;
  justify-content: space-between;
  font-weight: normal;
  font-size: 0.875rem;
  color: #6b7280;
}

.item-image-cell {
  width: 100px;
}

.item-image-container {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  color: #9ca3af;
}

.item-name-cell {
  font-weight: 500;
}

.reorder-cell {
  width: 200px;
}

.reorder-levels {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.min-level,
.max-level {
  flex: 1;
  text-align: center;
  padding: 0.5rem;
  background-color: #f3f4f6;
  border-radius: 4px;
  font-weight: 500;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #d1d5db;
}

/* Centered Loading Component */
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 16px;
  color: #007bff;
  gap: 10px;
}

/* Finance Tab Grid */
.finance-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.finance-row {
  display: flex;
  gap: 20px;
}

.finance-item {
  flex: 1;
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
}

/* Dealer Visit Frequency */
.visit-frequency-grid {
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
}

.day-box {
  flex: 1 1 12%;
  min-width: 80px;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px 10px;
  border-radius: 8px;
  background-color: #f3f4f6;
  color: #374151;
  font-weight: 500;
  text-align: center;
  border: 1px solid #374151;
  position: relative;
}

.day-box-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  width: 100%;
}

.day-box i.fa-calendar-day {
  font-size: 1.2rem;
}

.check-icon {
  position: absolute;
  top: -20px;
  right: -5px;
  background-color: #1c66ad;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
}

.day-box.active-border {
  border: 2px solid #1c66ad;
  background-color: #e0f2fe;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .creation-info-container {
    gap: 8px;
  }

  .info-box {
    padding: 3px 6px;
    font-size: 0.7rem;
  }

  .popup-columns {
    flex-direction: column;
  }

  .details-column,
  .documents-column {
    width: 100%;
    padding: 0;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
  }

  .section-tabs {
    padding: 0 1rem;
    overflow-x: auto;
  }

  .tab-btn-tabs {
    padding: 6px 12px;
    font-size: 0.9rem;
  }

  .popup-body {
    padding: 1rem;
  }

  .info-section {
    padding: 1rem;
  }

  .reorder-levels {
    flex-direction: column;
    gap: 0.5rem;
  }

  .visit-frequency-grid {
    gap: 8px;
  }

  .day-box {
    flex: 1 1 45%;
    min-width: 100px;
    min-height: 80px;
  }
}

@media (max-width: 480px) {
  .header-main {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .creation-info-container {
    flex-wrap: wrap;
    gap: 6px;
  }

  .info-box {
    flex: 1;
    min-width: 140px;
  }

  .documents-grid {
    grid-template-columns: 1fr;
  }

  .document-image-container {
    height: 180px;
  }

  .section-tabs {
    flex-direction: column;
  }

  .tab-btn-tabs {
    justify-content: center;
  }

  .day-box {
    flex: 1 1 100%;
  }
}

.full-row-item {
  width: 100%;
}
</style>
